// Bulk Reporting Tool report jobs (ASB-4295): how a job from /bulk-reports/jobs
// reads in the reports table and the toasts. Pure functions, so the rules the
// stories set (status labels, Expired, the next-visit toast) are unit-tested
// without a browser.

export const ACTIVE_STATUSES = ["queued", "running"];
export const isActiveJob = (job) => ACTIVE_STATUSES.includes(job?.status);

// The six sections in the order ASB-3996 lists them, with its labels.
export const SECTION_LABELS = {
	engagement: "Engagement",
	technographics: "Technographics",
	audience_overlap: "Audience Overlap",
	demographics: "Demographics",
	third_party: "Third Party",
	content_read: "Content Read",
};
const SECTION_ORDER = Object.keys(SECTION_LABELS);

// The status a row shows. "expired" is only written by the API when someone
// tries to download, so a succeeded job whose link has lapsed reads as Expired
// too: download_available is false and expires_at has passed.
export function jobDisplayStatus(job, now = Date.now()) {
	switch (job?.status) {
		case "queued":
			return "queued";
		case "running":
			return "generating";
		case "succeeded": {
			const expires = job.expires_at ? Date.parse(job.expires_at) : NaN;
			if (!job.download_available && Number.isFinite(expires) && expires < now) return "expired";
			return "ready";
		}
		case "failed":
			return "failed";
		case "cancelled":
			return "cancelled";
		case "expired":
			return "expired";
		default:
			return "unknown";
	}
}

export const STATUS_LABELS = {
	queued: "Queued",
	generating: "Generating",
	ready: "Ready",
	failed: "Failed",
	cancelled: "Cancelled",
	expired: "Expired",
	unknown: "Unknown",
};

export const STATUS_TONES = {
	queued: "neutral",
	generating: "info",
	ready: "success",
	failed: "error",
	cancelled: "muted",
	expired: "muted",
	unknown: "muted",
};

// Elapsed time since started_at: "45s", "2m 05s", "1h 02m". Empty when the
// start is unknown or in the future (clock skew).
export function formatElapsed(startedAt, now = Date.now()) {
	const start = startedAt ? Date.parse(startedAt) : NaN;
	if (!Number.isFinite(start) || now < start) return "";
	const total = Math.floor((now - start) / 1000);
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const pad = (n) => String(n).padStart(2, "0");
	if (h > 0) return `${h}h ${pad(m)}m`;
	if (m > 0) return `${m}m ${pad(s)}s`;
	return `${s}s`;
}

// The pill's text: "Generating 2m 05s" while running, else the label.
export function statusLabel(job, now = Date.now()) {
	const status = jobDisplayStatus(job, now);
	if (status !== "generating") return STATUS_LABELS[status];
	const elapsed = formatElapsed(job.started_at, now);
	return elapsed ? `${STATUS_LABELS.generating} ${elapsed}` : STATUS_LABELS.generating;
}

// "Mar 17, 2026", the short date the Manager and Signal pages print. The API's
// dates are calendar days, so read and print them in UTC.
export const formatDay = (date) =>
	date
		? new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
				year: "numeric",
				timeZone: "UTC",
		  })
		: "";

// "Oct 8, 2026, 12:00 PM" in the viewer's time zone.
export function formatDateTime(iso) {
	const t = iso ? Date.parse(iso) : NaN;
	if (!Number.isFinite(t)) return "";
	return new Date(t).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

// The sections a job asked for, in ASB-3996's order, by label.
export function sectionsLabel(sections) {
	const chosen = Array.isArray(sections) ? sections : [];
	return SECTION_ORDER.filter((key) => chosen.includes(key))
		.map((key) => SECTION_LABELS[key])
		.join(", ");
}

// The job's campaign as the API names it (ASB-4726): "Name (ID)" by GAM's
// current name, or the name a job made before the ID switch stored.
export const campaignLabel = (job) => job?.order_label || job?.spec?.order || "";

// ASB-3998: the toast a failed report shows on the user's next visit. The
// failures cover every ArcTag the user can use, so it names the report's
// campaign and ArcTag when they are known (ASB-4360).
export function failureToastMessage(job, { campaign, arcTag } = {}) {
	const which = [campaign && `for ${campaign}`, arcTag && `on ArcTag ${arcTag}`].filter(Boolean).join(" ");
	return (
		`The report ${which ? `${which} that ` : ""}you attempted to generate on ${formatDateTime(job?.queued_at)} ` +
		"via the Bulk Reporting Tool did not complete. We're sorry for the inconvenience. If you still want the " +
		"report, please recreate the report request now via the Bulk Reporting Tool. If it continues to fail, " +
		"please contact AMS support."
	);
}

// A spec filter value as a list: the API takes one value or a list.
export const specValues = (value) =>
	value === undefined || value === null || value === "" ? [] : Array.isArray(value) ? value : [value];
